import cx from 'clsx';
import React from 'react';
import { useFieldArray, useForm } from 'react-hook-form';

import { ReactComponent as AddIcon } from '@assets/svg/add.svg';
import { ReactComponent as DeleteIcon } from '@assets/svg/ic-remove.svg';

import EditorForm from '@components/form/EditorForm/EditorForm';
import ModalHeader from '@components/modals/ModalHeader/ModalHeader';
import Button from '@components/uikit/Button/Button';
import Input from '@components/uikit/Input/Input';

import { showToast } from '@helpers/showToast';

import useTranslation from '@hooks/useTranslation';

import type { IFile } from '@model/file';

import type { IEquipmentLink } from '@pages/SensorsList/model/equipmentLink';

import { useModalActions } from '@store/useModalStore';

import AttachmentsField from '../CompletionReportModal/components/AttachmentsField/AttachmentsField';
import {
  EDIT_EQUIPMENT_NOTES_FIELDS,
  editEquipmentNotesDefaultValue,
  emptyItem,
  schemaEditEquipmentNotes,
} from './constants/schemaEditEquipmentNotes';
import { useSaveEquipmentNotesMutation } from './services/useSaveEquipmentNotesMutation';
import style from './style.module.scss';

interface IProps {
  equipmentId: number;
  equipmentName: string;
  description: string | null;
  links: IEquipmentLink[] | null;
  files: IFile[] | null;
}

const EditEquipmentNotesModal: React.FC<IProps> = ({
  equipmentId,
  equipmentName,
  description,
  links,
  files,
}) => {
  const { t } = useTranslation();

  const { setModalClose } = useModalActions();

  const { mutate: saveEquipmentNotesMutate, isLoading } = useSaveEquipmentNotesMutation();

  const {
    control,
    handleSubmit,
    reset,
    formState: { isDirty, isSubmitting },
  } = useForm<{
    name: string;
    description: string;
    links: IEquipmentLink[];
    files: IFile[];
  }>({
    mode: 'onChange',
    resolver: schemaEditEquipmentNotes(),
    defaultValues: editEquipmentNotesDefaultValue(equipmentName, description, links, files),
  });

  const { fields, append, remove } = useFieldArray({
    name: EDIT_EQUIPMENT_NOTES_FIELDS.LINKS as 'links',
    control,
  });

  const submit = handleSubmit((formData) => {
    const links = formData.links.filter((l) => l.name || l.link);
    const files = formData.files;

    saveEquipmentNotesMutate(
      {
        equipment_id: equipmentId,
        name: formData.name,
        description: formData.description,
        links: links.length ? links : null,
        files: files.length ? files : null,
      },
      {
        onSuccess: () => {
          showToast({ variant: 'success', message: t('The equipment notes have been successfully saved.') });

          handleClose();
        },
      },
    );
  });

  const handleClose = () => {
    setModalClose();
    reset();
  };

  const disabledDelete = fields.length === 1;

  return (
    <div className={style.editEquipmentNotes}>
      <ModalHeader title={t('Edit notes for equipment')} />
      <div className={style.modalBody}>
        <div className={style.modalBodyForm}>
          <Input
            label={t('Equipment')}
            control={control}
            name={EDIT_EQUIPMENT_NOTES_FIELDS.NAME}
            type="woHelperText"
          />
          <EditorForm
            label={t('Description')}
            control={control}
            name={EDIT_EQUIPMENT_NOTES_FIELDS.DESCRIPTION}
            type="woHelperText"
            hideMedia={true}
          />
          <AttachmentsField name={EDIT_EQUIPMENT_NOTES_FIELDS.FILES} control={control} label="Attachments" />
          <div>
            <div className={style.linksTableWrapper}>
              <table className={style.linksTable}>
                <thead>
                  <tr>
                    <th>{t('Link Name')}</th>
                    <th>{t('Link to')}</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {fields.map((field, i) => (
                    <tr key={field.id} className={style.archivedAiBodyRow}>
                      <td>
                        <Input
                          name={`${EDIT_EQUIPMENT_NOTES_FIELDS.LINKS}[${i}][name]`}
                          control={control}
                          type={'woHelperText'}
                          placeholder={t('Name')}
                        />
                      </td>
                      <td>
                        <Input
                          name={`${EDIT_EQUIPMENT_NOTES_FIELDS.LINKS}[${i}][link]`}
                          control={control}
                          type={'woHelperText'}
                          placeholder={t('URL')}
                        />
                      </td>
                      <td className={style.deleteColumn}>
                        <DeleteIcon
                          className={cx(style.deleteIcon, { [style.disabledDelete]: disabledDelete })}
                          onClick={() => (disabledDelete ? null : remove(i))}
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className={style.addLinkWrapper}>
              <Button
                type="secondary"
                text={t('Add link')}
                leftElement={<AddIcon className={style.addIcon} />}
                onClick={() => append(emptyItem)}
              />
            </div>
          </div>
        </div>
      </div>
      <div className={style.modalFooter}>
        <Button type="secondary" text={t('Cancel')} disabled={isLoading} onClick={handleClose} />
        <Button
          type="primary"
          text={t('Save')}
          disabled={isSubmitting || !isDirty}
          onClick={submit}
          variant={'small'}
        />
      </div>
    </div>
  );
};

export default EditEquipmentNotesModal;
